import React, { useEffect, useMemo, useState } from 'react';
import api from '../../services/api.js';
import { useToast } from '../../context/ToastContext.jsx';
import { money } from '../../config/brand.js';

const STATUSES = ['pending', 'processing', 'shipped', 'delivered', 'cancelled'];

function exportCsv(orders) {
  const esc = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const rows = [['Order', 'Date', 'Status', 'Customer', 'Email', 'Phone', 'Address', 'Items', 'Subtotal', 'Shipping', 'Total']];
  orders.forEach((o) => rows.push([
    o.id, o.orderDate, o.status, o.customerName, o.customerEmail, o.customerPhone, o.shippingAddress,
    (o.items || []).map((i) => `${i.name}${i.size ? ` (${i.size})` : ''} x${i.quantity}`).join('; '),
    o.subtotal ?? o.total, o.shipping ?? 0, o.total,
  ]));
  const blob = new Blob([rows.map((r) => r.map(esc).join(',')).join('\n')], { type: 'text/csv' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = `orders-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function Orders() {
  const notify = useToast();
  const [orders, setOrders] = useState(null);
  const [status, setStatus] = useState('');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(null);
  const [error, setError] = useState('');

  const load = () => api.orders().then(setOrders).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);

  const list = useMemo(
    () => (orders || [])
      .filter((o) => !status || o.status === status)
      .filter((o) => !q || `${o.id} ${o.customerName} ${o.customerEmail} ${o.customerPhone}`.toLowerCase().includes(q.toLowerCase())),
    [orders, status, q]
  );

  const change = async (o, next) => {
    try { await api.setOrderStatus(o.id, next); notify('Status updated', 'success'); load(); } catch (e) { notify(e.message, 'error'); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Delete order ${o.id}? Stock is not returned unless the order was cancelled first.`)) return;
    try { await api.deleteOrder(o.id); notify('Order deleted', 'success'); load(); } catch (e) { notify(e.message, 'error'); }
  };

  if (error) return <p className="aerror">{error}</p>;
  return (
    <div>
      <div className="ahead">
        <h1 className="atitle">Orders</h1>
        <button type="button" className="abtn abtn--ghost" disabled={!list.length} onClick={() => exportCsv(list)}>Export CSV</button>
      </div>
      <div className="atools">
        <input placeholder="Search by order, name, email or phone" value={q} onChange={(e) => setQ(e.target.value)} />
        <select value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">All statuses</option>
          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>
      {!orders ? <p className="amuted">Loading</p> : list.length === 0 ? <p className="amuted">No orders found.</p> : (
        <div className="ascroll">
          <table className="atable">
            <thead><tr><th>Order</th><th>Date</th><th>Customer</th><th className="num">Total</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {list.map((o) => (
                <React.Fragment key={o.id}>
                  <tr>
                    <td><button type="button" className="linkbtn" onClick={() => setOpen(open === o.id ? null : o.id)}>{o.id}</button></td>
                    <td>{o.orderDate}</td>
                    <td>{o.customerName}<small>{o.customerPhone}</small></td>
                    <td className="num">{money(o.total)}</td>
                    <td>
                      <select value={o.status} onChange={(e) => change(o, e.target.value)} aria-label={`Status for ${o.id}`}>
                        {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td className="actions"><button type="button" className="linkbtn" onClick={() => remove(o)}>Delete</button></td>
                  </tr>
                  {open === o.id && (
                    <tr className="detail">
                      <td colSpan={6}>
                        <div className="adetail">
                          <div>
                            <h3>Customer</h3>
                            <p>{o.customerName}<br />{o.customerEmail}<br />{o.customerPhone}</p>
                            <h3>Delivery address</h3>
                            <p>{o.shippingAddress}</p>
                            {o.notes && (<><h3>Notes</h3><p>{o.notes}</p></>)}
                          </div>
                          <div>
                            <h3>Items</h3>
                            <table className="atable">
                              <thead><tr><th>Item</th><th>Size</th><th className="num">Qty</th><th className="num">Amount</th></tr></thead>
                              <tbody>
                                {(o.items || []).map((i, k) => (
                                  <tr key={k}><td>{i.name}</td><td>{i.size || '-'}</td><td className="num">{i.quantity}</td><td className="num">{money(i.price * i.quantity)}</td></tr>
                                ))}
                              </tbody>
                            </table>
                            <p className="amuted">Subtotal {money(o.subtotal ?? o.total)} / Shipping {o.shipping ? money(o.shipping) : 'Free'} / <strong>Total {money(o.total)}</strong></p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
